// Per-property tracks for the tavern → combat splice.
//
// Split out of SpliceRenderer so the visual math is testable without a DOM.
// That matters more here than it sounds: the guarantee that the seam is fully
// CLOSED at the moment the route swaps is a property of these tracks, not of
// the score alone — the score says when the cover beat ends, but only the track
// says what the cover's actual value is at `commitAt`. On the tape transport
// that was confirmed by scrubbing the real DOM in a browser; automating it here
// means it stays confirmed on every run, on every tier, in both familiarity
// variants.

import { beatEnd, type Stop } from './sequence';
import { easeFn } from './motion';
import type { SpliceScore } from './spliceTimeline';

export interface SpliceTracks {
  /** Guards the memo in the renderer — tracks are rebuilt when the score changes. */
  forTotal: number;
  /** 0 = takes off screen, 1 = met at the seam. Used when travelling. */
  shut: Stop[];
  /** Cover opacity for the reduced tier, which dissolves instead of sliding. */
  dissolve: Stop[];
  seam: Stop[];
  seamScale: Stop[];
  marks: Stop[];
  flash: Stop[];
  crews: Stop[];
}

export function buildSpliceTracks(s: SpliceScore): SpliceTracks {
  const shutAt = beatEnd(s.cover);
  const openEnd = s.total;

  return {
    forTotal: s.total,
    // The slam closes on `transport` so the halves arrive with weight rather
    // than easing politely into place.
    shut: [
      { at: 0, v: 0 },
      { at: s.cover.at, v: 0 },
      { at: shutAt, v: 1, ease: easeFn.transport },
      { at: s.settle.at, v: 1 },
      { at: openEnd, v: 0, ease: easeFn.out },
    ],
    dissolve: [
      { at: 0, v: 0 },
      { at: s.cover.at, v: 0 },
      { at: shutAt, v: 1, ease: easeFn.out },
      { at: s.settle.at, v: 1 },
      { at: openEnd, v: 0, ease: easeFn.out },
    ],
    seam: [
      { at: 0, v: 0 },
      { at: s.stop.at, v: 0.35, ease: easeFn.out },
      { at: shutAt, v: 1, ease: easeFn.out },
      { at: s.settle.at, v: 0.8 },
      { at: openEnd, v: 0, ease: easeFn.in },
    ],
    seamScale: [
      { at: 0, v: 0.3 },
      { at: beatEnd(s.stop), v: 1, ease: easeFn.out },
      { at: s.settle.at, v: 1 },
      { at: openEnd, v: 0.4, ease: easeFn.in },
    ],
    marks: [
      { at: 0, v: 0 },
      { at: s.marks.at, v: 0 },
      { at: s.marks.at + s.marks.dur * 0.35, v: 1, ease: easeFn.out },
      { at: beatEnd(s.marks), v: 0, ease: easeFn.in },
    ],
    flash: [
      { at: 0, v: 0 },
      { at: shutAt - 0.03, v: 0 },
      { at: shutAt, v: 1, ease: easeFn.out },
      { at: shutAt + 0.18, v: 0, ease: easeFn.in },
    ],
    crews: [
      { at: 0, v: 0 },
      { at: shutAt, v: 0 },
      { at: s.slam.at + s.slam.dur * 0.5, v: 1, ease: easeFn.out },
      { at: s.settle.at, v: 1 },
      { at: s.settle.at + s.settle.dur * 0.5, v: 0, ease: easeFn.in },
    ],
  };
}
